import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const emptyItem = {
  skuMappingId: '',
  serviceName: '',
  skuDescription: '',
  unit: '',
  uom: '',
  quantity: '',
  uomCost: '',
};

const lineTotal = (item) => Number(item.unit || 0) * Number(item.quantity || 0) * Number(item.uomCost || 0);

const mapReportToForm = (report) => ({
  clientOwnerId: report?.client_owner_id ? String(report.client_owner_id) : '',
  title: report?.title || 'All Services',
  periodFrom: report?.period_from || '',
  periodTo: report?.period_to || '',
  billingDate: report?.billing_date || report?.period_to || '',
  items: (report?.items || []).map((item) => ({
    skuMappingId: item.sku_mapping_id ? String(item.sku_mapping_id) : '',
    serviceName: item.service_name || '',
    skuDescription: item.sku_description || '',
    unit: item.unit ?? '',
    uom: item.uom || '',
    quantity: item.quantity ?? '',
    uomCost: item.uom_cost ?? '',
  })),
});

const money = (value) => {
  const num = Number(value || 0);
  return `₹ ${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (value) => {
  if (!value) return '';
  const text = String(value);
  const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const groupItems = (items = []) => items.reduce((acc, item) => {
  const key = item.service_name || item.serviceName || 'Service';
  if (!acc[key]) acc[key] = [];
  acc[key].push(item);
  return acc;
}, {});

function BillingPreview({ report }) {
  const grouped = groupItems(report?.items || []);
  const grandTotal = (report?.items || []).reduce((sum, item) => sum + Number(item.total_cost ?? item.totalCost ?? 0), 0);

  if (!report) {
    return <div style={s.empty}>No Data Found</div>;
  }

  return (
    <div style={s.tableCard}>
      <div style={s.tableHeader}>
        <h3 style={s.chartTitle}>
          {report.title || 'All Services'} — Cost Breakdown ({formatDate(report.billing_date || report.period_to)})
        </h3>
        {report.is_calculated
          ? <span style={{ ...s.statusBadge, ...s.published }}>Calculated</span>
          : report.status && <span style={{ ...s.statusBadge, ...(report.status === 'published' ? s.published : s.draft) }}>{report.status}</span>}
      </div>
      <div style={s.periodLine}>
        Period: {formatDate(report.period_from)} to {formatDate(report.period_to)}
      </div>

      <table style={s.billingTable}>
        <tbody>
          {Object.entries(grouped).map(([serviceName, items]) => {
            const serviceTotal = items.reduce((sum, item) => sum + Number(item.total_cost ?? item.totalCost ?? 0), 0);
            return (
              <React.Fragment key={serviceName}>
                <tr style={s.sectionRow}>
                  <td colSpan="5" style={s.sectionCell}>{serviceName}</td>
                  <td style={{ ...s.sectionCell, ...s.amountCell }}>{money(serviceTotal)}</td>
                </tr>
                <tr style={s.headRow}>
                  <th style={s.th}>SKU description</th>
                  <th style={s.th}>Unit</th>
                  <th style={s.th}>UoM</th>
                  <th style={s.th}>Qty</th>
                  <th style={s.th}>UoM Cost</th>
                  <th style={s.th}>Total Cost (Amount In Thousands)</th>
                </tr>
                {items.map((item, index) => (
                  <tr key={`${serviceName}-${index}`}>
                    <td style={s.td}>{item.sku_description ?? item.skuDescription}</td>
                    <td style={s.numTd}>{Number(item.unit ?? 0).toLocaleString('en-IN')}</td>
                    <td style={s.td}>{item.uom}</td>
                    <td style={s.numTd}>{Number(item.quantity ?? 0).toLocaleString('en-IN')}</td>
                    <td style={s.numTd}>{money(item.uom_cost ?? item.uomCost)}</td>
                    <td style={{ ...s.numTd, ...s.totalCell }}>{money(item.total_cost ?? item.totalCost)}</td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          <tr style={s.grandRow}>
            <td colSpan="5" style={s.grandCell}>Grand Total (Amount In Thousands)</td>
            <td style={{ ...s.grandCell, ...s.amountCell }}>{money(grandTotal)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function CsvBillingPage() {
  const { isAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [skuMappings, setSkuMappings] = useState([]);
  const [reports, setReports] = useState([]);
  const [activeReport, setActiveReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [filters, setFilters] = useState({ periodFrom: '', periodTo: '' });
  const [editingReportId, setEditingReportId] = useState(null);
  const [form, setForm] = useState({
    clientOwnerId: '',
    title: 'All Services',
    periodFrom: '',
    periodTo: '',
    billingDate: '',
    items: [{ ...emptyItem }],
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (!isAdmin) {
        if (filters.periodFrom) params.set('periodFrom', filters.periodFrom);
        if (filters.periodTo) params.set('periodTo', filters.periodTo);
      }
      const reportReq = api.get(`/billing/manual-reports${params.toString() ? `?${params.toString()}` : ''}`);
      const [reportRes, clientRes] = await Promise.all([
        reportReq,
        isAdmin ? api.get('/billing/manual-reports/clients') : Promise.resolve({ data: { clients: [] } }),
      ]);
      setReports(reportRes.data.reports || []);
      setActiveReport(reportRes.data.report || reportRes.data.reports?.[0] || null);
      setClients(clientRes.data.clients || []);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load billing dashboard');
    } finally {
      setLoading(false);
    }
  }, [filters.periodFrom, filters.periodTo, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!isAdmin || !form.clientOwnerId) {
      setSkuMappings([]);
      return;
    }

    api.get(`/google-usage/mappings?customerUserId=${form.clientOwnerId}`)
      .then(({ data }) => setSkuMappings(data.mappings || []))
      .catch(() => setSkuMappings([]));
  }, [form.clientOwnerId, isAdmin]);

  const resetForm = () => {
    setEditingReportId(null);
    setForm({
      clientOwnerId: '',
      title: 'All Services',
      periodFrom: '',
      periodTo: '',
      billingDate: '',
      items: [{ ...emptyItem }],
    });
  };

  const startEditingReport = (report) => {
    setEditingReportId(report.id);
    setForm({
      ...mapReportToForm(report),
      items: mapReportToForm(report).items.length ? mapReportToForm(report).items : [{ ...emptyItem }],
    });
  };

  const previewReport = useMemo(() => ({
    ...form,
    period_from: form.periodFrom,
    period_to: form.periodTo,
    billing_date: form.billingDate || form.periodTo,
    status: editingReportId ? (reports.find((report) => String(report.id) === String(editingReportId))?.status || 'draft') : 'draft',
    items: form.items.map((item) => ({
      service_name: item.serviceName,
      sku_description: item.skuDescription,
      skuMappingId: item.skuMappingId,
      unit: item.unit,
      uom: item.uom,
      quantity: item.quantity,
      uom_cost: item.uomCost,
      total_cost: lineTotal(item),
    })),
  }), [editingReportId, form, reports]);

  const updateItem = (index, key, value) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, i) => i === index ? { ...item, [key]: value } : item),
    }));
  };

  const selectSkuMapping = (index, mappingId) => {
    const mapping = skuMappings.find((item) => String(item.id) === String(mappingId));
    setForm((current) => ({
      ...current,
      items: current.items.map((item, i) => {
        if (i !== index) return item;
        if (!mapping) return { ...item, skuMappingId: mappingId };
        return {
          ...item,
          skuMappingId: mappingId,
          serviceName: mapping.serviceName || '',
          skuDescription: mapping.skuDescription || mapping.skuId || '',
          uom: mapping.purchasedUnit || '',
        };
      }),
    }));
  };

  const addItem = () => setForm((current) => ({ ...current, items: [...current.items, { ...emptyItem }] }));
  const removeItem = (index) => setForm((current) => ({
    ...current,
    items: current.items.filter((_, i) => i !== index),
  }));

  const saveDraft = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/billing/manual-reports', {
        ...form,
        id: editingReportId || undefined,
      });
      setSuccess(editingReportId ? 'Billing dashboard updated.' : 'Billing dashboard saved as draft. Publish it when ready for the client.');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save billing dashboard');
    } finally {
      setSaving(false);
    }
  };

  const publish = async (reportId) => {
    setSaving(true);
    try {
      await api.put(`/billing/manual-reports/${reportId}/publish`);
      setSuccess('Billing dashboard published to client.');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to publish billing dashboard');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.layout}>
      <Sidebar />
      <main style={s.main}>
        <div style={s.header}>
          <div>
            <div style={s.breadcrumb}>Billing</div>
            <h1 style={s.pageTitle}>GCP Billing Overview</h1>
            <p style={s.pageSubtitle}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div style={s.headerActions}>
            {lastRefresh && <span style={s.refreshTime}>Updated {lastRefresh.toLocaleTimeString()}</span>}
            <button style={s.outlineBtn} onClick={fetchData} disabled={loading}>⟳ Refresh</button>
          </div>
        </div>

        {error && <div style={s.errorBox}>{error}</div>}
        {success && <div style={s.successBox}>{success}</div>}

        {!isAdmin && (
          <div style={s.filterCard}>
            <label style={s.field}>
              <span style={s.label}>Period From</span>
              <input type="date" value={filters.periodFrom} onChange={(e) => setFilters({ ...filters, periodFrom: e.target.value })} style={s.input} />
            </label>
            <label style={s.field}>
              <span style={s.label}>Period To</span>
              <input type="date" value={filters.periodTo} onChange={(e) => setFilters({ ...filters, periodTo: e.target.value })} style={s.input} />
            </label>
            <button style={s.primaryBtn} onClick={fetchData} disabled={loading}>Apply Filter</button>
            <button style={s.smallBtn} onClick={() => setFilters({ periodFrom: '', periodTo: '' })}>Clear</button>
          </div>
        )}

        {isAdmin && (
          <div style={s.editorCard}>
            <div style={s.editorHeader}>
              <h3 style={s.editorTitle}>Admin Billing Entry</h3>
              <div style={s.editorHeaderActions}>
                {editingReportId && <span style={s.editingBadge}>Editing existing report</span>}
                <span style={s.draftNote}>Drafts are hidden from clients until published.</span>
              </div>
            </div>
            <div style={s.formGrid}>
              <label style={s.field}>
                <span style={s.label}>Client</span>
                <select value={form.clientOwnerId} onChange={(e) => setForm({ ...form, clientOwnerId: e.target.value })} style={s.input}>
                  <option value="">Select client</option>
                  {clients.map((client) => <option key={client.id} value={client.id}>{client.name} ({client.email})</option>)}
                </select>
              </label>
              <label style={s.field}>
                <span style={s.label}>Period From</span>
                <input type="date" value={form.periodFrom} onChange={(e) => setForm({ ...form, periodFrom: e.target.value })} style={s.input} />
              </label>
              <label style={s.field}>
                <span style={s.label}>Period To</span>
                <input type="date" value={form.periodTo} onChange={(e) => setForm({ ...form, periodTo: e.target.value })} style={s.input} />
              </label>
              <label style={s.field}>
                <span style={s.label}>Billing Date</span>
                <input type="date" value={form.billingDate} onChange={(e) => setForm({ ...form, billingDate: e.target.value })} style={s.input} />
              </label>
            </div>

            <div style={s.itemsHeader}>
              <span style={s.label}>Billing Details</span>
              <button style={s.smallBtn} onClick={addItem}>+ Add Row</button>
            </div>
            {form.items.map((item, index) => (
              <div key={index} style={s.itemGrid}>
                <select value={item.skuMappingId} onChange={(e) => selectSkuMapping(index, e.target.value)} style={s.input}>
                  <option value="">Select mapped SKU</option>
                  {skuMappings.map((mapping) => (
                    <option key={mapping.id} value={mapping.id}>
                      {(mapping.serviceName || 'Google Cloud')} - {(mapping.skuDescription || mapping.skuId)}
                    </option>
                  ))}
                </select>
                <input placeholder="Service name" value={item.serviceName} onChange={(e) => updateItem(index, 'serviceName', e.target.value)} style={s.input} />
                <input placeholder="SKU description" value={item.skuDescription} onChange={(e) => updateItem(index, 'skuDescription', e.target.value)} style={s.input} />
                <input placeholder="Unit" value={item.unit} onChange={(e) => updateItem(index, 'unit', e.target.value)} style={s.input} />
                <input placeholder="UoM" value={item.uom} onChange={(e) => updateItem(index, 'uom', e.target.value)} style={s.input} />
                <input placeholder="Qty" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', e.target.value)} style={s.input} />
                <input placeholder="UoM Cost" value={item.uomCost} onChange={(e) => updateItem(index, 'uomCost', e.target.value)} style={s.input} />
                <div style={s.calculatedTotal}>{money(lineTotal(item))}</div>
                <button style={s.removeBtn} onClick={() => removeItem(index)} disabled={form.items.length === 1}>×</button>
              </div>
            ))}
            <div style={s.actionRow}>
              {editingReportId && <button style={s.smallBtn} onClick={resetForm} disabled={saving}>Cancel Edit</button>}
              <button style={s.primaryBtn} onClick={saveDraft} disabled={saving}>{saving ? 'Saving...' : (editingReportId ? 'Save Changes' : 'Save Draft')}</button>
            </div>
          </div>
        )}

        {loading ? (
          <div style={s.empty}>Loading billing dashboard...</div>
        ) : (
          <BillingPreview report={isAdmin ? previewReport : activeReport} />
        )}

        {isAdmin && reports.length > 0 && (
          <div style={s.listCard}>
            <h3 style={s.editorTitle}>Saved Billing Dashboards</h3>
            {reports.map((report) => (
              <div key={report.id} style={s.reportRow}>
                <div>
                  <strong>{report.client_name}</strong>
                  <div style={s.rowMeta}>{formatDate(report.period_from)} to {formatDate(report.period_to)} · {report.status}</div>
                </div>
                <div style={s.rowActions}>
                  <button style={s.smallBtn} onClick={() => setActiveReport(report)}>Preview</button>
                  <button style={s.smallBtn} onClick={() => startEditingReport(report)}>Edit</button>
                  {report.status !== 'published' && <button style={s.publishBtn} onClick={() => publish(report.id)}>Publish</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const s = {
  layout: { display: 'flex', background: '#f5f7fa', minHeight: '100vh', fontFamily: "'Inter','Segoe UI',sans-serif" },
  main: { marginLeft: 0, flex: 1, padding: '12px 12px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  breadcrumb: { color: '#94a3b8', fontSize: 12, fontWeight: 500, marginBottom: 4 },
  pageTitle: { margin: 0, color: '#020617', fontSize: 30, fontWeight: 800 },
  pageSubtitle: { margin: '4px 0 0', color: '#94a3b8', fontSize: 16 },
  headerActions: { display: 'flex', alignItems: 'center', gap: 12 },
  refreshTime: { color: '#94a3b8', fontSize: 14 },
  outlineBtn: { background: '#fff', border: '1px solid #dbe3ef', color: '#0f172a', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontSize: 16 },
  errorBox: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', color: '#dc2626', marginBottom: 16 },
  successBox: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', color: '#166534', marginBottom: 16 },
  editorCard: { background: '#fff', border: '1px solid #dbe3ef', borderRadius: 12, padding: 20, marginBottom: 20 },
  editorHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  editorHeaderActions: { display: 'flex', alignItems: 'center', gap: 10 },
  editorTitle: { margin: 0, color: '#0f172a', fontSize: 17, fontWeight: 700 },
  draftNote: { color: '#64748b', fontSize: 13 },
  editingBadge: { background: '#e0f2fe', border: '1px solid #7dd3fc', color: '#075985', borderRadius: 20, padding: '4px 10px', fontSize: 12, fontWeight: 700 },
  formGrid: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 12, marginBottom: 18 },
  filterCard: { display: 'flex', alignItems: 'flex-end', gap: 12, background: '#fff', border: '1px solid #dbe3ef', borderRadius: 12, padding: 16, marginBottom: 18 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { color: '#475569', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' },
  input: { width: '100%', border: '1px solid #dbe3ef', borderRadius: 8, padding: '10px 12px', color: '#0f172a', fontSize: 13, boxSizing: 'border-box', background: '#fff' },
  itemsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  itemGrid: { display: 'grid', gridTemplateColumns: '1.4fr 1.1fr 1.7fr 0.7fr 0.8fr 0.7fr 0.8fr 1fr 36px', gap: 8, marginBottom: 8 },
  actionRow: { display: 'flex', justifyContent: 'flex-end', marginTop: 14 },
  primaryBtn: { background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontWeight: 700 },
  smallBtn: { background: '#fff', border: '1px solid #dbe3ef', color: '#0f172a', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13 },
  publishBtn: { background: '#dcfce7', border: '1px solid #86efac', color: '#166534', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontWeight: 700 },
  removeBtn: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, cursor: 'pointer', fontSize: 20, lineHeight: 1 },
  calculatedTotal: { background: '#f8fafc', border: '1px solid #dbe3ef', borderRadius: 8, padding: '10px 12px', color: '#0f172a', fontSize: 13, fontWeight: 700, textAlign: 'right' },
  tableCard: { background: '#fff', border: '1px solid #dbe3ef', borderRadius: 12, overflow: 'hidden', marginBottom: 20, boxShadow: '0 2px 8px rgba(15,23,42,0.05)' },
  tableHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '22px 26px', borderBottom: '1px solid #e5e7eb' },
  chartTitle: { margin: 0, color: '#020617', fontSize: 19, fontWeight: 800 },
  periodLine: { padding: '10px 26px', color: '#64748b', fontSize: 13, borderBottom: '1px solid #e5e7eb' },
  billingTable: { width: '100%', borderCollapse: 'collapse', color: '#020617' },
  sectionRow: { background: '#e8e6e6' },
  sectionCell: { padding: '18px 16px', fontWeight: 800, fontSize: 18 },
  amountCell: { textAlign: 'right' },
  headRow: { background: '#f6f6f6' },
  th: { textAlign: 'left', color: '#4b5563', fontSize: 16, padding: '14px 16px', borderBottom: '1px solid #d7d7d7' },
  td: { padding: '14px 16px', fontSize: 18, borderBottom: '1px solid #e5e7eb' },
  numTd: { padding: '14px 16px', fontSize: 18, borderBottom: '1px solid #e5e7eb', textAlign: 'right' },
  totalCell: { fontWeight: 500 },
  grandRow: { background: '#dfe8f4' },
  grandCell: { padding: '18px 16px', fontSize: 18, fontWeight: 800 },
  statusBadge: { borderRadius: 20, padding: '4px 10px', fontSize: 12, fontWeight: 700, textTransform: 'capitalize' },
  draft: { background: '#fef9c3', color: '#854d0e' },
  published: { background: '#dcfce7', color: '#166534' },
  empty: { background: '#fff', border: '1px solid #dbe3ef', borderRadius: 12, padding: 32, textAlign: 'center', color: '#64748b' },
  listCard: { background: '#fff', border: '1px solid #dbe3ef', borderRadius: 12, padding: 18 },
  reportRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f1f5f9' },
  rowMeta: { color: '#64748b', fontSize: 13, marginTop: 4 },
  rowActions: { display: 'flex', gap: 8 },
};
